import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll } from "framer-motion";
import "../styles/Expertise.css";
import knifeImg from "../assets/misc/knife.png";
import kittenImg from "../assets/misc/kitten.png";

const skills = [
  "Typescript & React",
  "Python, Node.js & REST APIs",
  "System Architecture",
  "DevOps & CI/CD",
  "Azure & AWS Cloud",
  "Linux Servers & Data Centers",
  "RAG Pipelines & LLM Integration",
  "Machine Learning & LoRA Training",
  "Project Management",
];

const KNIFE_HEIGHT = 520;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const getBladeProgress = (globalProgress, index, total) => {
  const spacing = 1 / (total + 0.4);
  const start = index * spacing * 0.8;
  const end = start + spacing * 1.2;
  const raw = (globalProgress - start) / (end - start);
  return easeOutCubic(Math.min(1, Math.max(0, raw)));
};

const useIsPortrait = () => {
  const [isPortrait, setIsPortrait] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mq = window.matchMedia("(orientation: portrait)");

    const handleChange = (e) => {
      setIsPortrait(e.matches);
    };

    setIsPortrait(mq.matches);

    if (mq.addEventListener) {
      mq.addEventListener("change", handleChange);
    } else {
      mq.addListener(handleChange);
    }

    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", handleChange);
      } else {
        mq.removeListener(handleChange);
      }
    };
  }, []);

  return isPortrait;
};

const SkillBlade = ({ skill, index, side, deployProgress, total }) => {
  const progress = getBladeProgress(deployProgress, index, total);
  const slide = side === "left" ? 36 : -36;

  return (
    <motion.div
      className={`expertise-skill expertise-skill--${side}`}
      animate={{
        opacity: progress,
        x: slide * (1 - progress),
      }}
      transition={{
        type: "spring",
        stiffness: 150,
        damping: 24,
      }}
    >
      {side === "left" ? (
        <>
          <span className="expertise-skill-pill">{skill}</span>
          <span
            className="expertise-skill-line"
            style={{ transform: `scaleX(${progress})` }}
          />
        </>
      ) : (
        <>
          <span
            className="expertise-skill-line"
            style={{ transform: `scaleX(${progress})` }}
          />
          <span className="expertise-skill-pill">{skill}</span>
        </>
      )}
    </motion.div>
  );
};

const Expertise = ({ onLoupeActiveChange, onLoupeSecretChange }) => {
  const sectionRef = useRef(null);
  const isPortrait = useIsPortrait();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [scrollDeploy, setScrollDeploy] = useState(0);
  const [hoverDeploy, setHoverDeploy] = useState(0);

  const deployProgress = Math.max(scrollDeploy, hoverDeploy);

  useEffect(() => {
    if (isPortrait) {
      setScrollDeploy(0);
      return;
    }

    const update = (value) => {
      setScrollDeploy(Math.min(1, Math.max(0, value)));
    };

    update(scrollYProgress.get());
    return scrollYProgress.on("change", update);
  }, [scrollYProgress, isPortrait]);

  useEffect(() => {
    if (isPortrait) {
      onLoupeActiveChange?.(false);
      onLoupeSecretChange?.(false);
    }
  }, [isPortrait, onLoupeActiveChange, onLoupeSecretChange]);

  const handlePointerMove = () => {
    if (isPortrait) return;
    onLoupeActiveChange?.(true);
    setHoverDeploy(1);
  };

  const handlePointerLeave = () => {
    setHoverDeploy(0);
    onLoupeActiveChange?.(false);
    onLoupeSecretChange?.(false);
  };

  const leftSkills = skills
    .map((skill, index) => ({ skill, index }))
    .filter(({ index }) => index % 2 === 0);

  const rightSkills = skills
    .map((skill, index) => ({ skill, index }))
    .filter(({ index }) => index % 2 === 1);

  if (isPortrait) {
    return (
      <section ref={sectionRef} className="expertise-section" id="expertise">
        <div className="expertise-inner">
          <header className="expertise-header">
            <p className="expertise-eyebrow">Expertise</p>
            <h2 className="expertise-title">
              Swiss-army knife for modern tech.
            </h2>
            <p className="expertise-subtitle">
              Quick overview of the stack I work with.
            </p>
          </header>

          <div className="expertise-knife-scene-mobile">
            <img
              src={knifeImg}
              alt="Swiss army knife"
              className="expertise-knife-img-mobile"
            />

            <motion.ul
              className="expertise-list-mobile"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              {skills.map((skill, index) => (
                <motion.li
                  key={skill}
                  className="expertise-list-mobile-item"
                  variants={{
                    hidden: { opacity: 0, y: 12 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.35,
                        delay: index * 0.1,
                        ease: "easeOut",
                      },
                    },
                  }}
                >
                  {skill}
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="expertise-section"
      id="expertise"
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <div className="expertise-inner">
        <header className="expertise-header">
          <p className="expertise-eyebrow">Expertise</p>
          <h2 className="expertise-title">
            Swiss-army knife for modern tech.
          </h2>
          <p className="expertise-subtitle">
            Quick overview of the stack I work with.
          </p>
        </header>

        <div
          className="expertise-knife-scene"
          style={{ "--knife-open": deployProgress }}
        >
          <div
            className="expertise-skills-col expertise-skills-col--left"
            style={{ height: KNIFE_HEIGHT }}
          >
            {leftSkills.map(({ skill, index }) => (
              <SkillBlade
                key={skill}
                skill={skill}
                index={index}
                side="left"
                deployProgress={deployProgress}
                total={skills.length}
              />
            ))}
          </div>

          <div
            className="expertise-knife-column"
            style={{ height: KNIFE_HEIGHT }}
          >
            <motion.div
              className="expertise-knife-glow"
              animate={{
                opacity: 0.4 + deployProgress * 0.5,
                scale: 1 + deployProgress * 0.12,
              }}
              transition={{ type: "spring", stiffness: 120, damping: 24 }}
            />
            <motion.img
              src={knifeImg}
              alt="Swiss army knife"
              className="expertise-knife-img"
              style={{ "--knife-glow": deployProgress }}
              animate={{
                scale: 0.96 + deployProgress * 0.04,
                opacity: 0.85 + deployProgress * 0.15,
              }}
              transition={{ type: "spring", stiffness: 100, damping: 22 }}
            />
          </div>

          <div
            className="expertise-skills-col expertise-skills-col--right"
            style={{ height: KNIFE_HEIGHT }}
          >
            {rightSkills.map(({ skill, index }) => (
              <SkillBlade
                key={skill}
                skill={skill}
                index={index}
                side="right"
                deployProgress={deployProgress}
                total={skills.length}
              />
            ))}
          </div>
        </div>

        <motion.img
          src={kittenImg}
          alt="Secret kitten"
          className="expertise-kitten"
          onPointerEnter={() => onLoupeSecretChange?.(true)}
          onPointerLeave={() => onLoupeSecretChange?.(false)}
        />
      </div>
    </section>
  );
};

export default Expertise;
